import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import config from 'src/config'
import {
  SuccessNotification,
  RejectedNotification,
} from 'src/notifications/SuccessNotification'
import Sentry from 'src/utils/sentry'

const initialState = {
  purchases: [],
  departments: [],
  isLoading: false,
  error: {},
  success: false,
}

const SpecialPurchasesSlice = createSlice({
  name: 'purchases',
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setPurchases: (state, action) => {
      state.purchases = action.payload
    },
    setDepartments: (state, action) => {
      state.departments = action.payload
    },
    addDepartment: (state, action) => {
      state.departments.push(action.payload)
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setSuccess: (state, action) => {
      state.success = action.payload
    },
  },
})

export const fetchAllSpecialPurchases = venueId => async dispatch => {
  dispatch(setIsLoading(true))
  try {
    const response = await API.get('api2', `specialPuurchases/${venueId}`)

    dispatch(setIsLoading(false))
    dispatch(setPurchases(response.data))
  } catch (e) {
    dispatch(setIsLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const sendApproveRequest = (formData, venueId) => async dispatch => {
  try {
    const session = await Auth.currentSession()
    const token = session.getAccessToken().getJwtToken()
    const response = await fetch(
      `${config.apiGateway.EXPRESS_URL}specialPuurchases/${venueId}/sendApproveRequest`,
      {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `${token}`,
        },
      },
    )
    let res = {}
    try {
      res = await response.json()
    } catch {
      RejectedNotification({ message: 'Failed to send approval request' })
      return false
    }
    if (response.ok && res.success) {
      SuccessNotification({ message: res?.message })
      dispatch(fetchAllSpecialPurchases(venueId))
      return true
    }
    RejectedNotification({
      message: res?.message || 'Failed to send approval request',
    })
    return false
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    RejectedNotification({ message: 'Failed to send approval request' })
    return false
  }
}

export const deleteSpecialPurchase =
  (purchaseId, venueId) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.del(
        'api2',
        `specialPuurchases/${venueId}/${purchaseId}`,
      )

      if (response.success) {
        dispatch(setSuccess(true))
        SuccessNotification({ message: response.message })
        dispatch(fetchAllSpecialPurchases(venueId))
      } else {
        dispatch(setError(response.error))
      }
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      dispatch(setError({ message: 'Failed to delete special purchase' }))
      return false
    }
  }

export const fetchDepartments = venueId => async dispatch => {
  try {
    const data = await API.get(
      'api2',
      `departments/${venueId}/fetchDepartments`,
    )
    dispatch(setDepartments(data?.departments || []))
  } catch (error) {
    console.error(error)
    Sentry.captureException(error)
  }
}

export const createDepartment = (venueId, departmentName) => async dispatch => {
  try {
    const response = await API.post(
      'api2',
      `departments/${venueId}/createDepartment`,
      {
        body: { departmentName },
      },
    )
    dispatch(addDepartment(response?.department))
    SuccessNotification({ message: 'Department created successfully' })
    return response?.department
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.data?.message ||
      (Array.isArray(error?.errors) && error?.errors?.[0]?.message) ||
      error?.message ||
      'Failed to create department'
    RejectedNotification({ message })
    Sentry.captureException(error)
    throw error
  }
}

export const {
  setIsLoading,
  setPurchases,
  setDepartments,
  addDepartment,
  setError,
  setSuccess,
} = SpecialPurchasesSlice.actions

export default SpecialPurchasesSlice
